import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Enhanced scroll reveal using GSAP ScrollTrigger.
 * Use instead of useScrollReveal when you need advanced effects
 * (parallax, scrub, pin, stagger children).
 *
 * @param {Object} options
 * @param {string} options.animation - 'fade-up'|'fade-down'|'fade-left'|'fade-right'|'scale'|'stagger-children'
 * @param {number} options.duration - seconds (default 0.8)
 * @param {string} options.ease - GSAP ease (default 'power2.out')
 * @param {boolean} options.scrub - link to scroll position (default false)
 * @param {string} options.start - ScrollTrigger start (default 'top 85%')
 * @param {number} options.staggerDelay - delay between children (default 0.1)
 * @param {boolean} options.disabled - skip animation
 * @returns {React.RefObject}
 */
export default function useGsapReveal(options = {}) {
    const ref = useRef(null);
    const {
        animation = 'fade-up',
        duration = 0.8,
        ease = 'power2.out',
        scrub = false,
        start = 'top 85%',
        staggerDelay = 0.1,
        disabled = false
    } = options;

    useEffect(() => {
        if (disabled || !ref.current) return;
        // Check reduced motion preference
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const el = ref.current;
        let ctx = gsap.context(() => {
            const fromVars = { opacity: 0 };
            const toVars = { opacity: 1, duration, ease };

            switch (animation) {
                case 'fade-up':
                    fromVars.y = 40;
                    toVars.y = 0;
                    break;
                case 'fade-down':
                    fromVars.y = -40;
                    toVars.y = 0;
                    break;
                case 'fade-left':
                    fromVars.x = -60;
                    toVars.x = 0;
                    break;
                case 'fade-right':
                    fromVars.x = 60;
                    toVars.x = 0;
                    break;
                case 'scale':
                    fromVars.scale = 0.85;
                    toVars.scale = 1;
                    break;
                case 'stagger-children':
                    // Animate direct children with stagger
                    gsap.fromTo(el.children,
                        { opacity: 0, y: 30 },
                        {
                            opacity: 1, y: 0, duration, ease,
                            stagger: staggerDelay,
                            scrollTrigger: { trigger: el, start, once: true }
                        }
                    );
                    return; // early return, don't create main tween
                default:
                    fromVars.y = 40;
                    toVars.y = 0;
            }

            toVars.scrollTrigger = scrub
                ? { trigger: el, start, end: 'bottom 20%', scrub: 1 }
                : { trigger: el, start, once: true };

            gsap.fromTo(el, fromVars, toVars);
        }, el);

        return () => ctx.revert();
    }, [animation, duration, ease, scrub, start, staggerDelay, disabled]);

    return ref;
}
